import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Copy, Pencil, Trash2, Search, Shield } from 'lucide-react';

const ROLE_LABEL = {
  student: 'Alumno', teacher: 'Profesor',
  school_admin: 'Admin escuela', partner_admin: 'Partner Admin', super_admin: 'Super Admin'
};
const ROLE_COLOR = {
  student: 'bg-emerald-100 text-emerald-700',
  teacher: 'bg-blue-100 text-blue-700',
  school_admin: 'bg-amber-100 text-amber-700',
  partner_admin: 'bg-violet-100 text-violet-700',
  super_admin: 'bg-rose-100 text-rose-700',
};

const empty = { email:'', password:'', first_name:'', last_name:'', role:'student' };

// Fase B-4B: helpers para vista previa de credenciales generadas
const slugify = (s) => (s||'')
  .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');
const ROLE_SUFFIX = { student: '', teacher: '.profesor', partner_admin: '.admin' };
const previewUsername = (fn, ln, role) => {
  const base = [slugify(fn), slugify(ln)].filter(Boolean).join('_');
  if (!base) return '';
  return `${base}${ROLE_SUFFIX[role] ?? ''}`;
};
const ROLE_LABEL_ES = { student: 'Alumno', teacher: 'Profesor', partner_admin: 'Partner Admin' };

// Fase B-4A: agrupación por pestañas. Solo filtra visualmente; usa el mismo /users.
const TAB_ROLES = {
  students: ['student'],
  teachers: ['teacher'],
  admins:   ['super_admin', 'partner_admin', 'assistant_admin', 'school_admin'],
};
const TAB_META = {
  students: { emoji: '👧', label: 'Alumnos',        newBtn: 'Nuevo alumno',         defaultRole: 'student' },
  teachers: { emoji: '👨‍🏫', label: 'Profesores',    newBtn: 'Nuevo profesor',       defaultRole: 'teacher' },
  admins:   { emoji: '🛡️', label: 'Administración', newBtn: 'Nuevo administrador', defaultRole: 'partner_admin' },
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const canSuper = me?.role === 'super_admin';
  const canPartner = me?.role === 'super_admin' || me?.role === 'partner_admin';
  const isAssistant = me?.role === 'assistant_admin';
  // Fase B-4C-2: assistant_admin nunca ve la pestaña Administración
  const visibleTabKeys = isAssistant ? ['students', 'teachers'] : ['students', 'teachers', 'admins'];
  const [tab, setTab] = useState('students');
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState('');

  // Create
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(empty);
  // Fase B-4B: resultado de creación (mostrar credenciales una vez)
  const [createdResult, setCreatedResult] = useState(null);

  // Edit
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState(null);

  // Delete
  const [delOpen, setDelOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Family invite
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ parent_email:'', parent_name:'' });
  const [inviteTarget, setInviteTarget] = useState(null);
  const [lastLink, setLastLink] = useState(null);

  const load = useCallback(() => {
    api.get('/users').then(r => setUsers(r.data));
    api.get('/admin/students-list').then(r => setStudents(r.data)).catch(()=>{});
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    if (!TAB_ROLES[tab].includes(u.role)) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (u.first_name||'').toLowerCase().includes(s) ||
           (u.last_name||'').toLowerCase().includes(s) ||
           (u.email||'').toLowerCase().includes(s);
  });

  const tabCounts = {
    students: users.filter(u => TAB_ROLES.students.includes(u.role)).length,
    teachers: users.filter(u => TAB_ROLES.teachers.includes(u.role)).length,
    admins:   users.filter(u => TAB_ROLES.admins.includes(u.role)).length,
  };

  const openCreate = () => {
    setCreateForm({ ...empty, role: TAB_META[tab].defaultRole });
    setCreateOpen(true);
  };

  const doCreate = async () => {
    const { first_name, last_name, role } = createForm;
    if (!first_name?.trim() || !last_name?.trim()) { toast.error('Nombre y apellido son obligatorios'); return; }
    if (!['student', 'teacher', 'partner_admin'].includes(role)) {
      toast.error('Rol no permitido en creación automática');
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/admin/users/generate', {
        first_name: first_name.trim(),
        last_name: last_name.trim(),
        role,
      });
      toast.success('Usuario creado');
      setCreateOpen(false);
      setCreateForm(empty);
      setCreatedResult(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear');
    } finally { setCreating(false); }
  };

  const openEdit = (u) => {
    setEditForm({
      id: u.id, email: u.email, first_name: u.first_name, last_name: u.last_name,
      role: u.role, active: u.active !== false, password: '', is_owner: !!u.is_owner
    });
    setEditOpen(true);
  };
  const doSave = async () => {
    if (!editForm) return;
    if (!editForm.first_name?.trim() || !editForm.last_name?.trim()) { toast.error('Nombre y apellido obligatorios'); return; }
    setSaving(true);
    const payload = {
      first_name: editForm.first_name.trim(),
      last_name: editForm.last_name.trim(),
      email: editForm.email,
      role: editForm.role,
      active: editForm.active,
    };
    if (editForm.password) payload.password = editForm.password;
    try {
      await api.patch(`/admin/users/${editForm.id}`, payload);
      toast.success('Usuario actualizado');
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const askDelete = (u) => { setDelTarget(u); setDelOpen(true); };
  const doDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${delTarget.id}`);
      toast.success('Usuario eliminado');
      setDelOpen(false);
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  const openInvite = (u) => {
    const s = students.find(x => x.user_id === u.id);
    if (!s) { toast.error('Este usuario no es un alumno'); return; }
    setInviteTarget({ ...u, student_id: s.id });
    setInvite({ parent_email:'', parent_name:'' });
    setLastLink(null);
    setInviteOpen(true);
  };
  const sendInvite = async () => {
    try {
      const { data } = await api.post('/family/invite', {
        student_id: inviteTarget.student_id,
        parent_email: invite.parent_email,
        parent_name: invite.parent_name
      });
      setLastLink(data.portal_url);
      toast.success('Invitación enviada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <PageHeader
        title="Usuarios"
        subtitle={`${tabCounts[tab]} en ${TAB_META[tab].label} · ${filtered.length} visibles`}
        testId="users-header"
        right={
          <Button data-testid="new-user-btn" onClick={openCreate}
            className="rounded-full bg-blue-600 hover:bg-blue-700">
            <UserPlus className="w-4 h-4 mr-1"/> {TAB_META[tab].newBtn}
          </Button>
        }
      />

      <div className="mb-5 border-b border-slate-200 overflow-x-auto -mx-2 px-2">
        <div className="flex gap-1 min-w-max">
          {visibleTabKeys.map((key) => {
            const m = TAB_META[key];
            return (
            <button
              key={key}
              data-testid={`users-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                tab === key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <span className="text-base leading-none">{m.emoji}</span>
              <span>{m.label}</span>
              <span className={`ml-1 inline-flex items-center justify-center min-w-[20px] px-1.5 h-5 rounded-full text-[11px] font-bold ${tab===key?'bg-blue-100 text-blue-700':'bg-slate-100 text-slate-600'}`}>{tabCounts[key]}</span>
            </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input data-testid="user-search" placeholder="Buscar por nombre o email…"
            value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9 rounded-full"/>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 font-bold">
            <tr>
              <th className="text-left p-3">Nombre</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Rol</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} data-testid={`user-${u.id}`} className="border-t hover:bg-slate-50">
                <td className="p-3 font-semibold flex items-center gap-2">
                  {u.is_owner && <Shield className="w-3.5 h-3.5 text-amber-500"/>}
                  {u.first_name} {u.last_name}
                </td>
                <td className="p-3 font-mono text-slate-600">{u.email}</td>
                <td className="p-3">
                  <Badge className={`${ROLE_COLOR[u.role]||''} border-0`}>{ROLE_LABEL[u.role]||u.role}</Badge>
                </td>
                <td className="p-3">
                  {u.active === false
                    ? <span className="text-xs font-semibold text-rose-600">Inactivo</span>
                    : <span className="text-xs font-semibold text-emerald-600">Activo</span>}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    {u.role === 'student' && (
                      <Button data-testid={`invite-parent-${u.id}`} size="sm" variant="outline"
                        className="rounded-full" onClick={()=>openInvite(u)}>
                        <UserPlus className="w-3.5 h-3.5 mr-1"/> Familia
                      </Button>
                    )}
                    <Button data-testid={`edit-user-${u.id}`} size="icon" variant="ghost"
                      className="rounded-full" onClick={()=>openEdit(u)}>
                      <Pencil className="w-4 h-4"/>
                    </Button>
                    {!u.is_owner && u.id !== me?.id && (
                      <Button data-testid={`delete-user-${u.id}`} size="icon" variant="ghost"
                        className="rounded-full text-rose-500 hover:bg-rose-50" onClick={()=>askDelete(u)}>
                        <Trash2 className="w-4 h-4"/>
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-slate-500 text-sm">Sin resultados.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader><DialogTitle>Crear {ROLE_LABEL_ES[createForm.role] || 'usuario'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nombre</Label>
                <Input data-testid="create-first-name" value={createForm.first_name}
                  onChange={(e)=>setCreateForm({...createForm, first_name:e.target.value})}/></div>
              <div><Label>Apellido</Label>
                <Input data-testid="create-last-name" value={createForm.last_name}
                  onChange={(e)=>setCreateForm({...createForm, last_name:e.target.value})}/></div>
            </div>
            <div><Label>Rol</Label>
              <Select value={createForm.role} onValueChange={(v)=>setCreateForm({...createForm, role:v})} disabled={isAssistant}>
                <SelectTrigger data-testid="create-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Alumno</SelectItem>
                  <SelectItem value="teacher">Profesor</SelectItem>
                  {canPartner && <SelectItem value="partner_admin">Partner Admin</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {/* Vista previa credenciales generadas */}
            <div data-testid="create-preview" className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Credenciales generadas</div>
              <div><span className="text-slate-500">Usuario:</span> <span data-testid="preview-username" className="font-mono">{previewUsername(createForm.first_name, createForm.last_name, createForm.role) || <span className="text-slate-400 italic">se genera al completar nombre y apellido</span>}</span></div>
              <div><span className="text-slate-500">Correo:</span> <span data-testid="preview-email" className="font-mono">{previewUsername(createForm.first_name, createForm.last_name, createForm.role) ? `${previewUsername(createForm.first_name, createForm.last_name, createForm.role)}@c4k.com` : <span className="text-slate-400 italic">—</span>}</span></div>
              <div><span className="text-slate-500">Contraseña:</span>{' '}
                {createForm.role === 'student'
                  ? <span data-testid="preview-password" className="font-mono font-bold">C4K</span>
                  : <span data-testid="preview-password" className="font-mono text-slate-500">•••••••• <span className="text-xs italic">(temporal, se muestra al crear)</span></span>}
              </div>
              {createForm.role !== 'student' && (
                <div className="text-xs text-amber-700 mt-1">El usuario deberá cambiar su contraseña al iniciar sesión.</div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setCreateOpen(false)}>Cancelar</Button>
            <Button data-testid="create-submit" onClick={doCreate} disabled={creating}
              className="rounded-full bg-blue-600 hover:bg-blue-700">
              {creating ? 'Creando…' : `Crear ${ROLE_LABEL_ES[createForm.role] || 'usuario'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result dialog (post-create) */}
      <Dialog open={!!createdResult} onOpenChange={(v)=>{ if(!v) setCreatedResult(null); }}>
        <DialogContent data-testid="created-result-dialog">
          <DialogHeader><DialogTitle>Credenciales del nuevo usuario</DialogTitle></DialogHeader>
          {createdResult && (
            <div className="space-y-3 text-sm">
              <div className="text-slate-500">Entrega estas credenciales al usuario. La contraseña temporal <b>no volverá a mostrarse</b>.</div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1 font-mono text-[13px]">
                <div><span className="text-slate-500 font-sans">Usuario:</span> <span data-testid="result-username">{createdResult.username}</span></div>
                <div><span className="text-slate-500 font-sans">Correo:</span> <span data-testid="result-email">{createdResult.email}</span></div>
                <div><span className="text-slate-500 font-sans">Contraseña:</span> <span data-testid="result-password" className="font-bold">{createdResult.temp_password}</span></div>
              </div>
              {createdResult.must_change_password && (
                <div className="text-xs text-amber-700">El usuario deberá cambiar esta contraseña temporal al iniciar sesión.</div>
              )}
              <Button
                data-testid="copy-credentials"
                variant="outline"
                className="rounded-full w-full"
                onClick={()=>{
                  const txt = `Usuario: ${createdResult.username}\nCorreo: ${createdResult.email}\nContraseña: ${createdResult.temp_password}`;
                  try {
                    navigator.clipboard?.writeText(txt);
                    toast.success('Credenciales copiadas');
                  } catch { toast.error('No se pudo copiar'); }
                }}
              >
                <Copy className="w-4 h-4 mr-1"/> Copiar credenciales
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="close-result" onClick={()=>setCreatedResult(null)}
              className="rounded-full bg-blue-600 hover:bg-blue-700">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Editar usuario</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre</Label>
                  <Input data-testid="edit-first-name" value={editForm.first_name}
                    onChange={(e)=>setEditForm({...editForm, first_name:e.target.value})}/></div>
                <div><Label>Apellido</Label>
                  <Input data-testid="edit-last-name" value={editForm.last_name}
                    onChange={(e)=>setEditForm({...editForm, last_name:e.target.value})}/></div>
              </div>
              <div><Label>Email</Label>
                <Input data-testid="edit-email" type="email" value={editForm.email}
                  onChange={(e)=>setEditForm({...editForm, email:e.target.value})}/></div>
              <div><Label>Rol</Label>
                <Select value={editForm.role} onValueChange={(v)=>setEditForm({...editForm, role:v})}
                  disabled={editForm.is_owner}>
                  <SelectTrigger data-testid="edit-role"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Alumno</SelectItem>
                    <SelectItem value="teacher">Profesor</SelectItem>
                    <SelectItem value="school_admin">Admin escuela</SelectItem>
                    {canPartner && <SelectItem value="partner_admin">Partner Admin</SelectItem>}
                    {canSuper && <SelectItem value="super_admin">Super Admin</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Restablecer contraseña (opcional)</Label>
                <Input data-testid="edit-password" type="password" placeholder="Dejar vacío para no cambiar"
                  value={editForm.password}
                  onChange={(e)=>setEditForm({...editForm, password:e.target.value})}/></div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="edit-active" checked={editForm.active}
                  onChange={(e)=>setEditForm({...editForm, active:e.target.checked})}/>
                Cuenta activa
              </label>
              {editForm.is_owner && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                  Este es el propietario. Puedes editar nombre/email pero no cambiar rol.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditOpen(false)}>Cancelar</Button>
            <Button data-testid="edit-submit" onClick={doSave} disabled={saving}
              className="rounded-full bg-blue-600 hover:bg-blue-700">
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle>Eliminar usuario</DialogTitle></DialogHeader>
          <div className="text-sm">
            Vas a eliminar a <b>{delTarget?.first_name} {delTarget?.last_name}</b> ({delTarget?.email}).
            <div className="mt-2 text-slate-500">Esta acción es permanente. Se borrarán su perfil, historial y notificaciones.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDelOpen(false)}>Cancelar</Button>
            <Button data-testid="delete-submit" onClick={doDelete} disabled={deleting}
              className="rounded-full bg-rose-600 hover:bg-rose-700">
              {deleting ? 'Eliminando…' : 'Eliminar definitivamente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Family invite */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invitar a la familia de {inviteTarget?.first_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="parent-email" type="email" placeholder="Email del padre/madre/tutor"
              value={invite.parent_email} onChange={(e)=>setInvite({...invite,parent_email:e.target.value})}/>
            <Input data-testid="parent-name" placeholder="Nombre (opcional)"
              value={invite.parent_name} onChange={(e)=>setInvite({...invite,parent_name:e.target.value})}/>
            {lastLink && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm">
                <div className="font-semibold text-emerald-800 mb-1">Portal creado</div>
                <div className="flex items-center gap-2">
                  <input readOnly value={lastLink} className="flex-1 bg-white text-xs font-mono p-2 rounded border"/>
                  <Button variant="outline" size="icon"
                    onClick={()=>{navigator.clipboard.writeText(lastLink);toast.success('Copiado');}}>
                    <Copy className="w-3.5 h-3.5"/>
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="parent-send-btn" onClick={sendInvite} disabled={!invite.parent_email}
              className="rounded-full bg-blue-600 hover:bg-blue-700">
              Enviar invitación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
